import { JWMButton } from "@components/JWMButton/JWMButton";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import {
    Color,
    HeaderHero,
    Size,
    Variant,
} from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";

interface Props {
    id: string;
    title?: string;
    pageTitle?: string;
    description?: string;
    image?: IGatsbyImageData;
    imagePosition?: ImagePosition;
    footer?: {
        buttonProps: React.ComponentProps<typeof Button>;
        link: { external?: boolean; url?: string; url_prefix?: string; title: string; anchor_id: string };
        text: string;
    }[];
    size?: Size;
    placement?: string;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    color?: Color;
    sidesColor?: Color;
    showGlossary?: boolean;
    variant?: Variant;
    children?: JSX.Element;
}

export const JWMHeaderHero = (props: Props): JSX.Element => {
    const {
        id,
        title,
        pageTitle,
        description,
        image,
        imagePosition,
        footer,
        size,
        placement,
        siteSpecialty,
        showGlossary,
        variant,
        color,
        sidesColor,
        children,
    } = props;

    const titleComponent = title ? (
        <JWMHtml color={"inherit"} showGlossary={false}>
            {title}
        </JWMHtml>
    ) : (
        pageTitle
    );

    const buttonColor = color === Color.PRIMARY ? Color.WHITE : Color.PRIMARY;
    const actions = footer?.map((button, index) => {
        if (button?.link?.title && button?.link?.url) {
            return (
                <JWMButton
                    key={index}
                    buttonProps={{
                        color: buttonColor,
                        variant: index === 0 ? "contained" : "naked",
                        flexibleWidth: index === 0 ? false : true,
                    }}
                    text={button?.text}
                    anchor_id={button?.link?.anchor_id}
                    url={button?.link?.url}
                    url_prefix={button?.link?.url_prefix}
                    fullWidth={false}
                    external={button?.link?.external}
                    placement={placement}
                />
            );
        }
    });

    const content = props.description ? (
        <JWMHtml
            color={"inherit"}
            size={"inherit"}
            placement={placement}
            siteSpecialty={siteSpecialty}
            showGlossary={showGlossary ?? true}
        >
            {description}
        </JWMHtml>
    ) : null;

    let aspectRatio = undefined;
    if (variant === Variant.MEDIA_SMALL && image) {
        aspectRatio = 4 / 3;
        image.height = 0.75;
    }

    const gatsbyImage = image ? (
        <GatsbyImage
            image={image}
            alt={title || ""}
            loading="eager"
            imgStyle={{ objectPosition: imagePosition?.replace("-", " "), aspectRatio }}
        />
    ) : null;

    return (
        <HeaderHero
            id={id}
            title={title}
            titleComponent={titleComponent}
            content={content}
            actions={actions}
            image={gatsbyImage}
            size={size}
            variant={variant}
            color={color}
            sidesColor={sidesColor}
        >
            {children}
        </HeaderHero>
    );
};
export default JWMHeaderHero;
